import { Segment } from '@dna/analytics'
import { RespondToViewport, WithProfiler } from '@dna/mixins'
import { addToBag, addedToBag } from '@dna/paths'
import * as paths from '@dna/paths'
import { storefrontClient } from '@dna/storefront'
import styles from '@dna/styles'
import { handleize, removeTextBeforeCharacterAndTrim, t } from '@dna/utils'
import {
  addToCart,
  getCategoryFromTags,
  getLineItemProperties,
  getMasterSku,
  minorToMajorUnit,
  renderPrice,
  splitProductName,
} from '@dna/utils'
import {
  LitElement,
  classMap,
  createRef,
  css,
  html,
  nothing,
  ref,
  repeat,
  when,
} from '@lit'
import { Task, TaskStatus } from '@lit/task'

if (!customElements.get('dna-quick-view')) {
  class DNAQuickView extends WithProfiler(RespondToViewport(LitElement)) {
    static properties = {
      quickViewOpen: { type: Boolean },
      product: { type: Object },
      addOnItems: { type: Array, state: true },
      selectedVariantAdded: { type: Boolean, state: true },
      activeSlide: { type: Number, state: true },
      colorOptions: { type: Array },
      allProductOptions: { type: Array },
      activeProductId: { type: Number },
      cartItems: { type: Array },
      maxOrderQuantity: { type: Number },
      ctaText: { type: String },
      sectionId: { type: String, attribute: 'section-id' },
      hasSizeOptions: { type: Boolean },
      available: { type: Boolean },
      fitTips: { type: Array },
      optionsArray: { type: Array },
      showSizeError: { type: Boolean },
      wishlistItems: { type: Array },
    }

    $form = createRef()
    $carousel = createRef()

    _addOnTask = new Task(this, {
      task: async ([productID]) => {
        if (!this.product?.id) return
        await fetch(
          `/recommendations/products.json?product_id=${productID}&intent=complementary`,
        )
          .then((res) => res.json())
          .then(({ products }) => {
            this.addOnItems = products
          })
      },
      args: () => [this.product?.id],
    })

    _variantTask = new Task(this, {
      task: async () => {
        const batchedProductVariants =
          await storefrontClient.getProductVariantsByBatch(this.optionsArray)

        for (let i = 0; i < this.colorOptions.length; i++) {
          const colorOption = this.colorOptions[i]
          const matchedVariant = batchedProductVariants.find(
            (product) => Number(product.id) === Number(colorOption.id),
          )

          if (matchedVariant?.variants) {
            this.colorOptions[i] = {
              ...colorOption,
              variants: matchedVariant.variants.map((variant) => ({
                ...variant,
                variant_title: variant.title,
                variant_id: variant.id,
              })),
            }
          }
        }
        return this.colorOptions
      },
      args: () => [],
    })

    _swatchTask = new Task(this, {
      task: async () => {
        if (!this.activeProductId) return
        const newProduct = await storefrontClient.getProductOptionsById(
          this.activeProductId,
        )
        const colorOptionValues = newProduct?.options?.colorOptions
        const sizeOptions = newProduct?.options?.sizeOptions
        this.hasSizeOptions = sizeOptions.length > 1

        const getSwatches = (tags) => {
          return (
            tags
              ?.find((tag) => tag.includes('storefront:swatchgroup:'))
              ?.split(':')
              .pop() || 'Color'
          )
        }
        const getPricing = (price, compareAtPrice) => {
          return {
            price: renderPrice(price),
            compareAtPrice: compareAtPrice ? renderPrice(compareAtPrice) : '',
            convertedPrice: renderPrice(price, {
              includeCurrencySymbol: false,
              convertToUSD: true,
            }),
            nonRenderedPrice: price ? Number(price) : '',
            nonRenderedCompareAtPrice: compareAtPrice
              ? Number(compareAtPrice)
              : '',
          }
        }

        this.colorOptions = colorOptionValues
          .filter((option) => option.onlineStoreUrl !== null)
          .map((option) => {
            const prices = getPricing(option?.price, option?.compareAtPrice)
            return {
              ...option,
              addOnItemsPresent: true,
              swatchGroup: getSwatches(option.tags),
              url:
                option.url ||
                `${window.location.origin}${Shopify.routes.root}products/${option.handle}`,
              value: option.color,
              ...prices,
            }
          })

        const currentProduct = this.colorOptions.find(
          (obj) => Number(obj?.id) === Number(this.activeProductId),
        )
        if (!this.product.id) {
          this.product = currentProduct
        }

        if (this.hasSizeOptions) {
          currentProduct.variants = sizeOptions
          this.product.variants = sizeOptions

          if (currentProduct.metafields?.rothys?.product_fit_tip) {
            this.product.metafields.rothys.product_fit_tip =
              currentProduct.metafields.rothys.product_fit_tip
          }
        }

        this.product.price = currentProduct.price
        if (
          currentProduct.nonRenderedPrice <
          currentProduct.nonRenderedCompareAtPrice
        ) {
          this.product.compareAtPrice = currentProduct.compareAtPrice
          this.product.nonRenderedCompareAtPrice =
            currentProduct.nonRenderedCompareAtPrice
        } else {
          this.product.compareAtPrice = ''
        }
        this.product.convertedPrice = currentProduct.convertedPrice
        this.product.nonRenderedPrice = currentProduct.nonRenderedPrice
        this.optionsArray = this.colorOptions.map((option) => option.id)
        this.activeVariantId = this.product.variant_id
        return this.colorOptions
      },
      args: () => [],
    })

    _cartTask = new Task(this, {
      task: async () => {
        const cartResult = await fetch(`${window.Shopify.routes.root}cart.js`)
        const cartData = await cartResult.json()
        this.cartItems = cartData.items
        return cartData
      },
      args: () => [],
    })

    get showAddOns() {
      const noSizeAddOns = this.addOnItems?.filter(
        (item) => item.variants.length === 1,
      ).length
      return this.product.addOnItemsPresent && noSizeAddOns !== 0
    }

    get maxOrdered() {
      let selectedId = this.product.variant_id
      if (this.activeVariantId && this.activeVariantId !== 0) {
        selectedId = this.activeVariantId
      }
      const itemInCart =
        this.cartItems.length > 0 &&
        this.cartItems.find(
          (item) => Number(item.variant_id) === Number(selectedId),
        )
      return itemInCart && itemInCart.quantity >= this.maxOrderQuantity
    }

    get specialMessage() {
      const metafields = this.product.metafields.rothys
      if (this.product.variants?.length > 0) {
        const selectedVariant = this.product.variants.find(
          (v) => Number(v.variant_id) === Number(this.activeVariantId),
        )
        if (
          selectedVariant?.metafields?.rothys?.preorder_backorder_experience
        ) {
          metafields.preorder_backorder_experience =
            selectedVariant?.metafields?.rothys?.preorder_backorder_experience
        }
        if (selectedVariant?.metafields?.rothys?.estimated_ship_date) {
          metafields.estimated_ship_date =
            selectedVariant?.metafields?.rothys?.estimated_ship_date
        }
      }

      if (!metafields) {
        return nothing
      }
      const preorder_backorder_experience =
        metafields.preorder_backorder_experience
      const estShipDate = metafields.estimated_ship_date
      const return_rules = metafields.return_rules

      if (
        preorder_backorder_experience === 'pre-order' ||
        preorder_backorder_experience === 'back-order'
      ) {
        let estShipDateMessage = ''
        let preorderBackorderMessage = ''
        if (estShipDate) {
          if (preorder_backorder_experience === 'pre-order') {
            preorderBackorderMessage = t('products.product.preorder')
          } else if (preorder_backorder_experience === 'back-order') {
            preorderBackorderMessage = t('products.product.backorder')
          }
          const shipStartDate = new Date(`${estShipDate}T12:00:00`)
          const shipEndDate = new Date(`${estShipDate}T12:00:00`)
          shipEndDate.setDate(shipEndDate.getDate() + 7)
          const startMonth = shipStartDate.toLocaleString('default', {
            month: 'short',
          })
          const startDay = shipStartDate.getDate()
          const startYear = shipStartDate.getFullYear()
          const endMonth = shipEndDate.toLocaleString('default', {
            month: 'short',
          })
          const endDay = shipEndDate.getDate()
          const endYear = shipEndDate.getFullYear()
          estShipDateMessage = `${startMonth.toUpperCase()} ${startDay}, ${startYear} - ${endMonth.toUpperCase()} ${endDay}, ${endYear}`
          if (
            (!this.activeVariantId || this.activeVariantId === 0) &&
            this.hasSizeOptions
          ) {
            return nothing
          }
          return html`<strong>${preorderBackorderMessage}:</strong> <span>${t('products.product.estimated_ship_date')} ${estShipDateMessage}`
        }
      } else if (return_rules === 'Final Sale') {
        return html`<strong>${t('products.product.final_sale.badge')}:</strong> <span>${t('products.product.final_sale.message')}`
      }
      return nothing
    }

    get comingSoon() {
      if (Object.keys(this.product.metafields).length !== 0) {
        return (
          this.product?.metafields?.rothys?.preorder_backorder_experience ===
          'coming-soon'
        )
      }
      if (this.product.variants?.length > 0) {
        const selectedVariant = this.product.variants.find(
          (v) => Number(v.variant_id) === Number(this.activeVariantId),
        )
        return (
          selectedVariant?.metafields?.rothys?.preorder_backorder_experience ===
          'coming-soon'
        )
      }
      return false
    }

    get variantUnselected() {
      if (
        (!this.activeVariantId || this.activeVariantId === 0) &&
        this.hasSizeOptions
      ) {
        return true
      }
      return false
    }

    get productMedia() {
      return this.product?.media?.filter(
        (media) => !media.alt.includes('PLP |'),
      )
    }

    get fitTip() {
      const fitTipMetafield = this.product.metafields?.rothys?.product_fit_tip
      const reference = this.fitTips?.find(
        (tip) => fitTipMetafield === tip.reference,
      )
      return reference?.short_text
    }

    get isWishlisted() {
      for (const item of this.wishlistItems) {
        if (
          Number(item.empi) === Number(this.activeProductId) &&
          Number(item.epi) === Number(this.activeVariantId)
        ) {
          return true
        }
      }
      return false
    }

    constructor() {
      super()
      this._setMobileBreakpoints(['mobile', 'tablet'])
      this.quickViewOpen = false
      this.product = {}
      this.addOnItems = []
      this.selectedVariantAdded = false
      this.activeSlide = 0
      this.colorOptions = []
      this.allProductOptions = []
      this.activeProductId = 0
      this.cartItems = []
      this.maxOrderQuantity = __DNA_GLOBAL__.b2cMaxOrderQuantity
      this.ctaText = ''
      this.sectionId = ''
      this.available = true
      this.fitTips = []
      this.optionsArray = []
      this.showSizeError = false
      this.wishlistItems = []

      document.addEventListener('dna:cart:refresh', this._refreshCartItems)
    }

    connectedCallback() {
      super.connectedCallback()
      document.addEventListener(
        `dna:quick-view:${this.sectionId}:open`,
        (event) => {
          this._handleQuickViewOpen(event)
        },
      )
      window.addEventListener('popstate', this._closeQuickView)

      const swymCallbackFn = (swat) => {
        document.addEventListener(
          WishlistPlusListUIContext.CustomEvents?.SwymWishlistFetched,
          (event) => {
            const defaultList = event.detail.defaultList
            this.wishlistItems = defaultList.listcontents
          },
        )
      }

      if (!window.SwymCallbacks) {
        window.SwymCallbacks = []
      }
      window.SwymCallbacks.push(swymCallbackFn)
    }

    disconnectedCallback() {
      document.removeEventListener('dna:cart:refresh', this._refreshCartItems)
      document.removeEventListener(
        `dna:quick-view:${this.sectionId}:open`,
        this._handleQuickViewOpen,
      )
      window.removeEventListener('popstate', this._closeQuickView)
    }

    _closeQuickView = () => {
      this.activeSlide = 0
      this.quickViewOpen = false
      this.selectedVariantAdded = false
      this.activeVariantId = 0
      if (!this.isMobile) {
        document.dispatchEvent(new CustomEvent('dna:main-menu:show'))
      }
    }

    _refreshCartItems = async (event) => {
      this.cartItems = event?.detail?.items
      this._addOnTask.run()
    }

    async _getProperties(item, variantID) {
      const product = await storefrontClient.getProductByHandle(item.handle)
      const variant = product.variants.find(
        (variant) => variant.id === variantID,
      )
      const properties = getLineItemProperties(product, variant)
      return properties
    }

    async _handleAddToCart(event) {
      event.preventDefault()
      let variant_id = this.product.variant_id
      if (this.hasSizeOptions) {
        variant_id = Number(this.activeVariantId)
      }
      this.product.compareAtPrice = this.nonRenderedCompareAtPrice
      const properties = await this._getProperties(this.product, variant_id)

      if (this.variantUnselected) {
        this.showSizeError = true
        return
      }
      if (!this.maxOrdered && this.available && !this.comingSoon) {
        addToCart({ id: variant_id, quantity: 1, properties }, false)
        setTimeout(() => {
          this.quickViewOpen = false
          document.dispatchEvent(new CustomEvent('dna:cart:open'))
        }, 500)
      }

      if (this.maxOrdered) {
        this.quickViewOpen = false
        document.dispatchEvent(new CustomEvent('dna:cart:open'))
      }
      this._trackQuickView('PRODUCT_CTA_CLICKED')
    }

    async _handleAddOnAddToCart(event, item) {
      event.preventDefault()
      const variant_id = item.variants[0].id
      this.selectedVariantAdded = true
      this.selectedVariantID = variant_id
      const properties = await this._getProperties(item, variant_id)
      if (item.compare_at_price && Number(item.compare_at_price) !== 0) {
        properties._compare_at_price = item.compare_at_price
      }
      await addToCart({ id: variant_id, quantity: 1, properties }, false)
      this._trackAddOns('PRODUCT_ADDONS_CTA_CLICKED', item)
    }

    _handleSlideChange = (event) => {
      this.activeSlide = event.detail.index
    }

    _handleOptionChange = () => {
      // set active product id and active variant id on each time even if its empty
      const formData = new FormData(this.$form.value)
      const values = Object.fromEntries(formData.entries())
      this.activeProductId = values.id
      this.product = this.colorOptions.find(
        (v) => v.id === this.activeProductId,
      )
      if (
        this.product.nonRenderedPrice >= this.product.nonRenderedCompareAtPrice
      ) {
        this.product.compareAtPrice = ''
      }
      this.available = this.product.available
      if (values.size) {
        const selectedVariant = this.product.variants?.find(
          (v) => v.variant_title === values.size,
        )
        this.activeVariantId = selectedVariant.variant_id
        this.available = selectedVariant.available
        this.showSizeError = false
      }
      if (!this.hasSizeOptions) {
        this.activeVariantId = this.product.variant_id
      }
      this.requestUpdate()
      this.$carousel.value.goToSlide(0)
    }

    _handleDetailsClicked() {
      sessionStorage.setItem('referrer', this.product.url)
      this._trackQuickView('QUICK_VIEW_FULL_DETAILS_CLICKED')
    }

    _handleQuickViewOpen = (event) => {
      const { detail } = event
      this.product = detail.product
      this.activeProductId = this.product.id
      if (!this.quickViewOpen) {
        this.quickViewOpen = true
        if (!this.isMobile) {
          document.dispatchEvent(new CustomEvent('dna:main-menu:hide'))
        }
        this._swatchTask
          .run()
          .then(() => {
            return this._variantTask.run()
          })
          .catch((error) => {
            console.error('An error occurred:', error)
          })
        this._trackQuickView('QUICK_VIEW_OPENED')
      }
    }

    _trackQuickView = (eventName, variantID) => {
      const analyticsData = {
        category: splitProductName(this.product?.title).style,
        name: this.product?.color,
        price: this.product?.nonRenderedPrice,
        product_category: this.product?.tags
          ? getCategoryFromTags(this.product?.tags)
          : undefined,
        product_id: this.product?.id,
        shopify_product_id: this.product?.id,
        sku_short: getMasterSku(this.product?.sku),
        url: this.product?.url,
      }
      if (eventName === 'SWATCH_CLICKED') {
        analyticsData.brand = Segment.CONSTANTS.BRAND
        analyticsData.image_url = this.product?.media[0]?.url
        analyticsData.in_stock = this.product?.available
        analyticsData.quantity = 1
        analyticsData.shopify_variant_id = this.product?.variant_id
        analyticsData.sku = this.product?.sku
      }
      if (eventName === 'VARIANT_CLICKED') {
        const selectedVariant = this.product.variants.find(
          (v) => Number(v.variant_id) === Number(variantID),
        )
        analyticsData.brand = Segment.CONSTANTS.BRAND
        analyticsData.image_url = this.product?.media[0]?.url
        analyticsData.in_stock = selectedVariant?.available
        analyticsData.quantity = 1
        analyticsData.shopify_variant_id = selectedVariant?.id
        analyticsData.sku = selectedVariant?.sku
        analyticsData.sku_short = getMasterSku(selectedVariant?.sku)
        analyticsData.variant = selectedVariant?.variant_title
      }
      if (eventName === 'PRODUCT_CTA_CLICKED') {
        analyticsData.product_cta_active_product = this.product?.title
        analyticsData.product_cta_text = this.ctaText
        analyticsData.product_cta_context = 'Quick View'
      }

      Segment.protect(() => {
        Segment.track(Segment.CONSTANTS.EVENTS[eventName], analyticsData)
      })
    }

    _trackAddOns = (eventName, item) => {
      const itemVariantId = item.variants.length > 1 ? '' : item.variants[0].id
      const analyticsData = {
        clicked_product_addon_category: splitProductName(item.title)?.style,
        clicked_product_addon_name: splitProductName(item.title)?.color,
        clicked_product_addon_price: Number(minorToMajorUnit(item.price)),
        clicked_product_addon_product_category: getCategoryFromTags(item.tags),
        clicked_product_addon_product_id: item.id?.toString(),
        clicked_product_addon_variant_id: itemVariantId
          ? itemVariantId.toString()
          : undefined,
      }
      Segment.protect(() => {
        Segment.track(Segment.CONSTANTS.EVENTS[eventName], analyticsData)
      })
    }

    _renderProductMedia = () => {
      return html` <div class="quick-view__images">
      <sl-carousel 
        ${ref(this.$carousel)} 
        id="quick-view__carousel" 
        pagination 
        navigation 
        @sl-slide-change=${this._handleSlideChange}
        >
        ${repeat(this.productMedia, (media, index) => {
          return html`
              ${
                !media.alt.includes('PLP |')
                  ? html`
                <sl-carousel-item>
                 ${
                   media.type === 'image'
                     ? html`
                    <dna-image
                      aspect-ratio="1"
                      src=${media.url}
                      width="100%"
                      sizes="(max-width: 768px) 375px, (min-width: 768px) 660px"
                      loading=${index === 0 || index === 1 ? 'eager' : 'lazy'}
                      fetch-priority=${index === 0 || index === 1 ? 'high' : 'auto'}
                    ></dna-image>
                  `
                     : media.type === 'video'
                       ? html`
                   <media-controller>
                          <video
                            id="video-element"
                            slot="media"
                            src=${media.url}
                            aria-label=${removeTextBeforeCharacterAndTrim(media.alt, '|')}
                            muted
                            playsinline
                            autoplay
                            loop
                          >
                          </video>
                          <media-poster-image
                            slot="poster"
                            src="${media.url}">
                          </media-poster-image>
                          <media-control-bar>
                            <media-play-button>
                              <span slot="play">${paths.play}</span>
                              <span slot="pause">${paths.pause}</span>
                            </media-play-button>
                          </media-control-bar>
                        </media-controller>
                  `
                       : ''
                 }
                </sl-carousel-item>
              `
                  : ''
              }`
        })}
          <span class="product-carousel__arrow" ?hidden=${this.activeSlide === 0 || this.isMobile} slot="previous-icon">
            ${paths.arrowLeft2}
          </span>
          <span class="product-carousel__arrow" ?hidden=${this.activeSlide === this.productMedia.length - 1 || this.isMobile} slot="next-icon">
            ${paths.arrowRight2}
          </span>
        </sl-carousel>
      </div>`
    }

    _renderAddOnItems = () => {
      return html`
    <span class="quick-view__addon-title strong uppercase">Pairs With:</span>
    <div class="quick-view__addon">
    <div class="quick-view__addon-items">
    ${repeat(this.addOnItems, (item) => {
      const variant = item.variants.find((v) => v.available === true)
      const isItemAdded =
        this.selectedVariantAdded && this.selectedVariantID === variant.id
      return html`
        <div class="quick-view__addon-item"
          data-cnstrc-product-detail
          data-cnstrc-item-id="${item.id}"
          data-cnstrc-item-variation-id="${variant.id}"
          data-cnstrc-item-name="${item.title}"
        >
        <a @click=${() => this._trackAddOns('PRODUCT_ADDONS_CLICKED', item)} href=${window.location.origin}${Shopify.routes.root}products/${
          item.handle
        }>
          <dna-image
            aspect-ratio="1"
            src=${item.featured_image}
            alt=${item.title}
            width="100%"
            loading="eager"
            sizes="160px"
          ></dna-image>
          </a>
          <div class="quick-view__addon-item-content">
            <span class="p3 strong">${splitProductName(item.title).style}</span>
            <span class="p3">${splitProductName(item.title).color}</span>
            <p
              class="quick-view__addon-price flex p3"
              data-cnstrc-item-price="${renderPrice(variant.price, {
                includeCurrencySymbol: false,
                convertToUSD: true,
              })}"
            >
                ${when(
                  variant.compare_at_price,
                  () => html`<s>${renderPrice(variant.compare_at_price)}</s>`,
                  () => nothing,
                )}
                <span class="${
                  variant.compare_at_price ? 'has-compare-at' : ''
                }">${renderPrice(variant.price)}</span>
              </p>
              <button 
                class="quick-view__addon-cta ${isItemAdded ? 'added' : ''}" 
                 @click=${(event) => {
                   this._handleAddOnAddToCart(event, item)
                 }}
                  data-cnstrc-btn="add_to_cart"
                  >
                ${when(
                  isItemAdded,
                  () =>
                    html`<span class="quick-view__addon-cta-icon">${addedToBag}</span>
                    <p class="quick-view__addon-cta-text">${t('products.product.add_ons.added')}</p>`,
                  () => html`<span class="quick-view__addon-cta-icon">${addToBag}</span>
                    <p class="quick-view__addon-cta-text">${t('products.product.add_to_cart')}</p>`,
                )}
                
              </button>
          </div>
         </div>`
    })}
    </div></div>`
    }

    _renderProductContent = () => {
      let showConstructorAttribute = false
      if (this.variantUnselected) {
        this.ctaText = t('products.product.unselected')
      } else if (!this.available) {
        this.ctaText = t('products.product.sold_out')
      } else if (this.maxOrdered) {
        this.ctaText = t('products.product.view_cart')
      } else if (this.comingSoon) {
        this.ctaText = t('products.product.coming_soon')
      } else {
        this.ctaText = t('products.product.add_to_cart')
        showConstructorAttribute = true
      }
      return html`<div class="quick-view__content">
     <div class="quick-view__content-title h5"> ${splitProductName(this.product.title).style} </div>
        <div class="quick-view__content-price p1"
        data-cnstrc-item-price="${this.product?.convertedPrice}"
        >
        ${when(
          !!this.product.compareAtPrice,
          () => html`<s>
            ${when(
              window.Shopify.currency.active !== 'USD',
              () => html`${window.Shopify.currency.active} `,
              () => nothing,
            )}
            ${this.product.compareAtPrice}
          </s>`,
          () => nothing,
        )}
        <span class="${this.product.compareAtPrice ? 'has-compare-at' : ''}">
  ${when(
    window.Shopify.currency.active !== 'USD',
    () => html`${window.Shopify.currency.active} `,
    () => nothing,
  )}
  ${this.product.price}
</span>
        </div>
        <form ${ref(this.$form)} @change=${this._handleOptionChange} class="quick-view__content-form">
          ${this._renderSwatches()} 
          ${when(
            !!this.hasSizeOptions,
            () => this._renderSizes(),
            () => nothing,
          )}
        </form>
        <div class="quick-view__content-message">${this.specialMessage}</div>
        <div class="quick-view__content-form">
          <sl-button
            variant=${this.available && !this.maxOrdered && !this.comingSoon && !this.variantUnselected ? 'primary' : 'secondary'}
            type="submit"
            name="add"
            class="quick-view__content-cta" w100
            @click=${(event) => {
              this._handleAddToCart(event)
            }}
            ?disabled=${(!this.available && !this.variantUnselected) || this.comingSoon}
            data-cnstrc-btn=${showConstructorAttribute ? 'add_to_cart' : nothing}
          >
            ${this.ctaText}
          </sl-button>
       <swymcs-wishlist-button id="swymcs-wishlist-pdp-button" class="swymcs-wishlist-button ${this.isWishlisted ? 'swym-added-custom' : ''}" data-cnstrc-btn="add_to_wishlist">
      <Button
        id="swymcs-wishlist-button"
        class="swymcs-wishlist-button swymcs-wishlist-button-${this.activeProductId}"
        data-empi="${this.activeProductId}"
        data-epi="${this.activeVariantId}"
        data-du="${this.product.url}"
        data-price="${this.product.price}"
        data-product-category="${getCategoryFromTags(this.product.tags)}"
        data-title="${this.product.title}"
        style="
          cursor: pointer !important;
          position: relative;
          margin-top: 8px;
          margin-left: 5px;
          z-index: 5;
          width: 40px; 
          height: 40px; 
          padding: 5px; 
          font-size: 16px; 
          cursor: pointer; 
          opacity: 1 !important; 
          color: #ffffff; 
          background: none; 
          border: unset; 
          display: flex; 
          justify-content: center;"
      >
        <div id="swymUnfilled" 
             style="
                height: 100%; 
                display: flex; 
                justify-content: center; 
                align-items: center;"
        >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 14 14" fill="${this.isWishlisted ? '#03143B' : 'none'}">
              <path d="M9.64922 2.1881C7.87406 2.1881 6.99906 3.9381 6.99906 3.9381C6.99906 3.9381 6.12406 2.1881 4.3489 2.1881C2.90625 2.1881 1.76382 3.39505 1.74906 4.83524C1.71898 7.82474 4.12058 9.95071 6.75297 11.7374C6.82554 11.7867 6.91128 11.8131 6.99906 11.8131C7.08683 11.8131 7.17258 11.7867 7.24515 11.7374C9.87726 9.95071 12.2789 7.82474 12.2491 4.83524C12.2343 3.39505 11.0919 2.1881 9.64922 2.1881Z" stroke="#03143B" stroke-width="0.65" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        </div>
      </Button>
    </swymcs-wishlist-button>
        </div>
        <sl-button @click=${this._handleDetailsClicked} href="${this.product.url}" variant="underline" class="quick-view__content-details">
        ${t('sections.quick_view.full_details')}
        </sl-button>
      </div>`
    }

    _renderQuickViewContent() {
      if (
        this._swatchTask.status === TaskStatus.ERROR ||
        this._swatchTask.status === TaskStatus.PENDING ||
        !this.product
      ) {
        return nothing
      }
      return html`
       <div class="quick-view js-quick-view" 
        data-cnstrc-product-detail
        data-cnstrc-item-id="${this.activeProductId}"
        data-cnstrc-item-variation-id="${this.activeVariantId}"
        data-cnstrc-item-name="${this.product?.title} - ${this.product?.color}"
        >
        <button @click=${this._closeQuickView} class="close-icon">${paths.close}</button>
        ${when(this.productMedia?.length > 1, this._renderProductMedia, () => nothing)}
      <div class="quick-view__container">
        <div class="quick-view__content-container">
          ${when(this.product.title, this._renderProductContent, () => nothing)}
          ${when(this.showAddOns, this._renderAddOnItems, () => nothing)}
        </div>
        </div>
      </div>
    `
    }

    _renderSwatches() {
      const hasSelectedValue = this.colorOptions.find((option) => {
        return Number(option.id) === Number(this.activeProductId)
      })
      const swatchGroup = hasSelectedValue ? hasSelectedValue.swatchGroup : ''
      const currentColor = hasSelectedValue ? hasSelectedValue.value : ''

      return html`
       <fieldset>
      <legend class="legend p2">
        <span
          class=${classMap({
            strong: true,
            uppercase: true,
            'legend-label': true,
            'legend-label--has-value': hasSelectedValue,
          })}
          >${swatchGroup}</span
        >
        ${when(
          hasSelectedValue,
          () => html`<span>${currentColor}</span>`,
          () => nothing,
        )}
      </legend>
        ${repeat(this.colorOptions, (option, index) => {
          const productId = option.id || this.activeProductId
          const swatchFilename = `/cdn/shop/files/${handleize(option.color)}.png`
          const inputName = `${option.handle}-${index}`
          return html`
            <input
              key=${productId}
              type="radio"
              id=${inputName}
              name="id"
              value=${productId}
              ?checked=${Number(this.activeProductId) === Number(productId)}
              class="swatch"
              @click="${() => this._trackQuickView('SWATCH_CLICKED')}"
            />
            <label
             for=${inputName}
            >
            <img src=${swatchFilename} alt=${option.color} width="32" height="32" loading="eager" />
              <span class="sr-only">${option.color}</span>
            </label>
          `
        })}
      </fieldset>`
    }

    _renderSizes() {
      return html`
      <fieldset class="quick-view__form-sizes">
        <legend class="legend p2">
          <span
          class=${classMap({
            strong: true,
            uppercase: true,
            'legend-label': true,
            'legend-label--error': this.showSizeError,
          })}>
            Size
          </span>
        </legend>
          ${repeat(this.product?.variants, (variant, index) => {
            const inputName = `${this.product.handle}${index}`
            return html`
            <input
              key=${variant.variant_title}
              type="radio"
              id=${inputName}
              name="size"
              value=${variant.variant_title}
              ?checked=${Number(this.activeVariantId) === Number(variant.id)}
              @click="${() => this._trackQuickView('VARIANT_CLICKED', variant.variant_id)}"
            />
            <label
             class=${classMap({ 'out-of-stock': !variant.available })}
             for=${inputName}
            >
              <span class="option-value">${variant.variant_title}</span>
            </label>
          `
          })}
      </fieldset>
      <div class="quick-view__form-fit-tip">${this.fitTip}</div>
      `
    }

    render() {
      return html`
      ${
        this.isMobile
          ? html`
          <sl-drawer @sl-hide=${this._closeQuickView} placement="bottom" .open=${this.quickViewOpen} style="--size: calc(100vh - 100px);">
            ${this._renderQuickViewContent()}
          </sl-drawer>`
          : html`
          <sl-dialog @sl-hide=${this._closeQuickView} .open=${this.quickViewOpen}>
            ${this._renderQuickViewContent()}
          </sl-dialog>`
      }
    `
    }

    static styles = [
      styles.flex,
      styles.typography,
      styles.shoelace,
      css`
      :host sl-drawer::part(body) {
        background-color: #F4F2EF;
        padding: 0;
      }
      :host sl-drawer::part(base) {
        z-index: 9999999999;
      }
      :host sl-dialog::part(header),
      :host sl-drawer::part(header) {
        display: none;
      }
      :host sl-dialog {
          --width: 90vw;
      }
      @media screen and (min-width: 1200px) and (max-width: 1525px) {
        :host sl-dialog {
          --width: 80vw;
        }
      }
      @media screen and (min-width: 1526px) and (max-width: 1640px) {
        :host sl-dialog {
          --width: 75vw;
        }
      }
      @media screen and (min-width: 1641px) {
        :host sl-dialog {
          --width: 70vw;
        }
      }
      :host sl-dialog::part(body) {
        background-color: #F4F2EF;
      }
      :host sl-dialog::part(base) {
        z-index: 9999999999;
      }
      :host sl-dialog::part(panel) {
        height: 90vh;
      }
      @media screen and (min-width: 1024px) {
        :host sl-dialog::part(body) {
          padding: 34px 32px 30px 55px
        }
      }
      .quick-view__content-container {
        background-color: #F4F2EF;
        position: relative;
      }
      @media screen and (min-width: 1024px) {
        .quick-view__content-container {
          background-color: #FFFFFF;
          overflow-y: scroll;
          overflow-x: hidden;
          margin-bottom: 24px;
        }

        .quick-view__content-container::-webkit-scrollbar {
          width: 4px;
        }

        ::-webkit-scrollbar-thumb {
          background-color: #C0C0C0;
          border-radius: 10px;
        }
      }
      .close-icon {
        background-color: var(--dna-color-sandstone);
        border: none;
        color: var(--dna-color-midnight);
        padding: 0;
        right: 12px;
        margin-top: 12px;
        z-index: 1;
        cursor: pointer;
        position: fixed;
        border-radius: 50%;
      }
      @media screen and (min-width: 1024px) {
        .close-icon {
          right: 4px;
          position: absolute;
          top: 4px;
          margin-top: 0px;
          padding-top: 12px;
          padding-left: 12px;
          padding-right: 12px;
          padding-bottom: 8px;
          background-color: var(--dna-brand-reverse);
        }
      }
      .close-icon svg {
        height: 16px;
        width: 16px;
        padding-top: 12px;
        padding-left: 12px;
        padding-right: 12px;
        padding-bottom: 8px;
      }
      @media screen and (min-width: 1024px) {
        .close-icon svg {
          padding: 0;
        }
      }
      @media screen and (min-width: 1024px) {
      .quick-view__container {
          margin-bottom: 24px;
          background-color: var(--dna-brand-reverse);
        }    
      }
      .quick-view__content {
        padding-top: 18px;
        padding-left: 16px;
      }    
      @media screen and (min-width: 1024px) {
      .quick-view__content {
          padding-top: 40px;
          padding-left: 24px;
        }    
      }
      @media screen and (min-width: 1024px) {
        .quick-view__container {
            height: 100%;
            display: flex;
            flex-flow: column;
        }
      }
      @media screen and (min-width: 1024px) and (max-width: 1200px) {
        .quick-view__container {
            flex: 0 0 42%;
          }
      }     
      @media screen and (min-width: 1024px) and (max-width: 1200px) {
        .quick-view__images {
          flex: 0 0 58%;
        }
      } 
      @media screen and (min-width: 1201px) and (max-width: 1400px) {
        .quick-view__container {
            flex: 0 0 40%;
          }
      }     
      @media screen and (min-width: 1201px) and (max-width: 1400px) {
        .quick-view__images {
          flex: 0 0 60%;
        }
      } 
      @media screen and (min-width: 1401px) {
        .quick-view__container {
            flex: 0 0 35%;
          }
      }     
      @media screen and (min-width: 1401px) {
        .quick-view__images {
          flex: 0 0 65%;
        }
      } 
      .quick-view {
        position: relative;
      }
      @media screen and (min-width: 1024px) {
        .quick-view {
          display: flex;
          height: 100%;
          padding-top: 0;
        }
      }
      #quick-view__carousel {
        --aspect-ratio: 1/1;
      }
      @media screen and (max-width: 1024px) {
        #quick-view__carousel img {
          width: 100%;
          height: 100%;
        }
      }
      @media screen and (min-width: 1024px) and (max-width: 1200px) {
        #quick-view__carousel {
          margin-right: 30px;
        }
      }
      @media screen and (min-width: 1021px) and (max-width: 1400px) { 
        #quick-view__carousel {
          margin-right: 40px;
        }
      }
      @media screen and (min-width: 1401px) {
        #quick-view__carousel {
          margin-right: 80px;
        }
      }
      #quick-view__carousel::part(pagination) {
        gap: 16px;
        margin-top: 16px;
      }
      @media screen and (min-width: 1024px) {
        #quick-view__carousel::part(pagination) {
          margin-top: 20px;
          margin-bottom: 40px;
        }
      }
      #quick-view__carousel::part(pagination-item) {
        width: 6px;
        height: 6px;
        background-color: transparent;
        border: solid 1px var(--dna-color-midnight);
      }
      #quick-view__carousel::part(pagination-item--active) {
        background-color: var(--dna-color-midnight);
        transform: scale(1);
      }
      #quick-view__carousel::part(navigation-button) {
        position: absolute;
        color: var(--dna-color-midnight);
      }
      #quick-view__carousel::part(navigation-button--previous) {
        left: 8px;
      }
      #quick-view__carousel::part(navigation-button--next) {
        right: 8px;
      }
      .product-carousel__arrow svg {
        width: 40px;
      }
      #quick-view__carousel::part(base) {
        gap: 0;
      }
      #quick-view__carousel dna-image {
        height: 100%; 
        width: 100%;
      }
      #quick-view__carousel media-controller {
        height: 100%;
        width: 100%;
      }
      #quick-view__carousel video {
        height: 100%;
        width: 100%;
      }
      @media screen and (min-width: 1024px) {
        .quick-view__content-title {
          width: 90%;
        } 
      } 
      @media screen and (min-width: 1024px) {
        .quick-view__content-price {
          margin-top: 4px;
        } 
      } 
      .quick-view__content-price s {
        display: inline-block;
      }
      .has-compare-at {
        color: #DA3522;
        margin-left: 4px;
      }
      .quick-view__content-color {
        font: var(--dna-body-p3-font);
        margin-top: 16px;
      }
      @media screen and (min-width: 1024px) {
        .quick-view__content-color {
          margin-top: 24px;
        }  
      }
      .quick-view__content-color--label {
        letter-spacing: 2px;
      }
      .quick-view__content-form {
        display: flex;
        margin-top: 4px;
        flex-flow: wrap;
      }
      @media screen and (min-width: 1024px) {
        .quick-view__content-form {
          padding-right: 24px;
        }
      }
      .legend {
        margin-bottom: 12px;
      }
      .legend-label {
        letter-spacing: 2px;
      }
      .legend-label--error {
        color: var(--dna-utility-attention)
      }
      .legend-label--has-value::after {
        content: ':';
      }
      fieldset {
        border: none;
        display: flex;
        flex: 0 0 98%;
        flex-wrap: wrap;
        gap: 8px;
        margin: 0 0 24px 0;
        max-width: 37rem;
        min-inline-size: auto;
        padding: 0;
        min-inline-size: auto;
        border: none;
        margin: 0 0 24px 0;
      }
      .quick-view__form-sizes {
        margin-bottom: 12px;
      }
      @media (min-width: 768px) {
        fieldset {
          min-width: fit-content;
        }
      }
      .quick-view__form-fit-tip {
        font: var(--dna-body-font);
      }
      input[type='radio'] {
        clip: rect(0, 0, 0, 0);
        overflow: hidden;
        position: absolute;
        height: 1px;
        width: 1px;
      }
      /* Swatch button styles */
      input[type='radio'].swatch + label {
        cursor: pointer;
        background-color: var(--dna-color-light-grey, lightgrey);
        border-radius: 50%;
        color: transparent;
        height: 32px;
        overflow: hidden;
        width: 32px;
        display: block;
      }
      @media (min-width: 1024px) {
       input[type='radio'].swatch + label {
        height: 20px;
        width: 20px;
       }
      }
      input[type='radio'].swatch + label > img {
        height: 100%;
        object-fit: contain;
        width: 100%;
      }
      input[type='radio'].swatch:checked + label,
      input[type='radio'].swatch + label:hover {
        outline: 1px solid var(--dna-color-midnight, black);
        outline-offset: 2px;
      }
      /* OOS color swatch styles
        input[type='radio'].swatch.out-of-stock + label {
      } */
      /* Option button (non-swatch) styles */
      input[type='radio']:not(.swatch) + label {
        cursor: pointer;
        --border-color: var(--dna-color-light-grey, grey);
        display: inline-block;
        width: 48px;
        height: 48px;
        border: 1px solid var(--border-color);
        color: var(--dna-color-midnight);
        line-height: 48px;
        text-align: center;
        transition: border var(--dna-duration-short) ease;
        margin-right: 4px;
      }
      input[type='radio']:not(.swatch):checked + label,
      input[type='radio']:not(.swatch) + label:hover {
        --border-color: var(--dna-color-midnight, black);
      }
      input[type='radio']:not(.swatch).out-of-stock + label {
        position: relative;
        overflow: hidden;
      }
      input[type='radio']:not(.swatch).out-of-stock + label > .option-value {
        opacity: 0.5;
      }
        #size-chart-trigger {
        cursor: pointer;
        z-index: var(--dna-z-index-content, 100);
      }
      @media (min-width: 768px) and (max-width: 991px) {
        #size-chart-trigger {
          margin-right: 10px;
        }
      }
      @media (min-width: 1440px) {
        #size-chart-trigger {
          margin-right: 5px;
        }
      }
      .option-buttons-wrapper {
        {{ option_buttons_wrapper_styles }}
      }
      /* breakpoint-tablet */
      @media (min-width: 768px) {
        fieldset {
          min-width: fit-content;
        }
        input[type='radio']:not(.swatch) + label {
          width: 46px;
          height: 36px;
          line-height: 36px;
          margin-right: 0px;
        }
        .option-buttons-wrapper {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-right: 0;
            padding-right: 0;
            overflow: visible;
        }
      }
      @media (min-width: 992px) and (max-width: 1439px) {
        input[type='radio']:not(.swatch) + label {
            width: 52px;
            height: 40px;
            line-height: 40px;
        }
      }
      .out-of-stock {
        position: relative;
      }
      .out-of-stock::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(
          to bottom right,
          transparent calc(50% - 1px),
          var(--border-color),
          transparent calc(50% + 1px)
        );
      }
      /* OOS color swatch styles
        input[type='radio'].swatch.out-of-stock + label {
      } */
      input[type='radio']:not(.swatch).out-of-stock + label {
        position: relative;
        overflow: hidden;
      }
      input[type='radio']:not(.swatch).out-of-stock + label > .option-value {
        opacity: 0.5;
      }
      .quick-view__content-message {
        margin-top: 24px;
        font: var(--dna-body-p3-font);
        margin-right: 36px;
      }
      .quick-view__content-message strong {
        text-transform: uppercase;
        letter-spacing: 2px;
      }
      .quick-view__content-cta {
        text-transform: uppercase;  
      }
      .quick-view__content-form sl-button::part(label) {
        text-transform: uppercase;
      }
      .quick-view__content-form {
        margin-top: 16px;
        margin-bottom: 16px;
        display: flex;
      }
      @media screen and (min-width: 1024px) {
        .quick-view__content-form {
          margin-top: 12px;
          margin-bottom: 24px;
        }
      }
      .quick-view__content-cta {
        width: 80%;
      }
      @media screen and (min-width: 1024px) {
        .quick-view__content-cta {
          width: 85%;
        }
      }
      .quick-view__content-form status-favorite-button {
        transform: scale(1.5);
        margin-top: 15px;
        margin-left: 14px;
      }
      .quick-view__content-details {
        margin-bottom: 16px;
      }
      .quick-view__content sl-button[variant='underline']::part(label) {
        font-size: 12px;
      }
      .quick-view__addon-title {
        margin-left: 16px;
        margin-right: 24px;
        letter-spacing: 2px;
        font-size: 12px;
      }
      @media screen and (min-width: 1024px) {
       .quick-view__addon-title {
          margin-left: 24px;
        }
      }
      .quick-view__addon {
        margin: 16px;
      }
       .quick-view__addon::-webkit-scrollbar {
        width: 4px;
       }
      .quick-view__addon::-webkit-scrollbar-thumb {
        background-color: rgba(193, 193, 193, 0.5);
        border-radius: 10px;
       }  
      .quick-view__addon::-webkit-scrollbar-track {
        background-color: transparent;

       }
      @media screen and (min-width: 1024px) {
        .quick-view__addon {
          margin: 10px 24px 0px 24px;
        }
      }
      .quick-view__addon-items {
        display: flex;
        flex-flow: wrap;
      }
      .quick-view__addon-item {
        flex: 0 0 50%;
        background-color: #F4F2EF;
        display: flex;
        flex-flow: column;
      }
      .quick-view__addon-item {
        flex: 0 0 49%;
        background-color: #F4F2EF;
        border-bottom: 1px solid var(--dna-color-light-grey);
        border-right: 1px solid var(--dna-color-light-grey);
      }
      .quick-view__addon-item:nth-child(odd) {
        border-left: 1px solid var(--dna-color-light-grey);
      } 
      .quick-view__addon-item:nth-child(1),
      .quick-view__addon-item:nth-child(2) {
        border-top: 1px solid var(--dna-color-light-grey);
      } 
      .quick-view__addon-item-content {
        margin: 10px;
        margin-bottom: 0px;
        display: flex;
        flex-direction: column;
        flex-grow: 1;
      }
      .quick-view__addon-price {
        margin: 0;
      }
      .quick-view__addon-cta {
        cursor: pointer;
        display: flex;
        color: var(--dna-color-midnight);
        font-size: 11px;
        font-weight: 500;
        letter-spacing: 2px;
        text-decoration: underline;
        text-transform: uppercase;
        width: 100%;
        align-items: center;
        margin: 0;
        padding: 0;
        border: 0;
        background-color: transparent;
        margin-top: auto;
      }
      .quick-view__addon-cta.added {
        text-decoration: none;
      }
      .quick-view__addon-cta-icon svg {
        height: 100%;
        width: 100%;
      }
      .quick-view__addon-cta-text {
        margin-left: 6px;
        margin-bottom: 16px;
        text-align: left;
      }
    `,
    ]
  }

  customElements.define('dna-quick-view', DNAQuickView)
}
